import React, { useState, useEffect, Fragment } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { isBlank, notBlank } from "../util/ext";
import { lensProp, set } from "ramda";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import FormControl from "@material-ui/core/FormControl";
import FormHelperText from "@material-ui/core/FormHelperText";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import PhotoOutlinedIcon from "@material-ui/icons/PhotoOutlined";
import Select from "@material-ui/core/Select";

import FileUploadInput from "components/FileUploadInput";
import ImageCrop from "../creator-application/common/ImageCrop";
import MediaDialog from "../creator-application/common/MediaDialog";

const imgSize = 350;

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
  },

  preview: {
    marginRight: theme.spacing(4),
    minWidth: 200,
    minHeight: 200,
    display: "flex",
    gap: 10,
    flexDirection: "column",
    alignItems: "center",
  },

  image: {
    width: 350,
    height: 350,
    borderRadius: "20px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.15)",
    objectFit: "contain",
  },

  thumbnailOverlay: {
    height: 200,
    width: 200,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "20px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.15)",
    backgroundColor: "#E5E5E5",
  },

  emptyThumbnail: {
    fontSize: "120px",
    color: theme.palette.primary.disabled,
  },

  input: {
    display: "flex",
    flexDirection: "column",
    gap: 15,
    flexGrow: 1,
    maxWidth: 450,
  },

  title: {
    fontSize: "1.6rem",
    lineHeight: "1.7rem",
    color: theme.palette.primary.darker,
    fontWeight: theme.typography.fontWeightBold,
  },

  subtitle: {
    fontSize: "1.2rem",
    marginBottom: 0,
  },

  fileTypes: {
    fontSize: "1.2rem",
    marginTop: 0,
    marginBottom: theme.spacing(2),
    fontStyle: "italic",
  },
}));

const UploadBadgeDialog = ({ onCancel, onSave, visible, uploading }) => {
  const classes = useStyles();
  const [error, setError] = useState({});
  const [type, setType] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState();
  const [thumbnailFile, setThumbnailFile] = useState();
  const [cropper, setCropper] = useState(null);
  const [aspectRatio, setAspectRatio] = useState(null);

  const onTypeChange = event => {
    const type = event.target.value;
    setType(type);
    setAspectRatio(type === "award" ? 1 : null);
  };

  useEffect(() => {
    if (cropper !== null) {
      cropper.setAspectRatio(aspectRatio);
    }
  }, [aspectRatio, cropper]);

  const handleCropperInitialized = cropper => setCropper(cropper);

  const onFileInputChange = file => {
    setThumbnailFile(file);
    if (notBlank(file)) {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        setThumbnailUrl(dataUrl);
      };
      reader.readAsDataURL(file);
      setError({});
    } else {
      setThumbnailUrl(null);
    }
  };

  const reset = () => {
    setError({});
    setType("");
    setThumbnailFile(null);
    setThumbnailUrl(null);
  };

  const handleSave = () => {
    if (isBlank(thumbnailFile) || isBlank(type)) {
      if (isBlank(thumbnailFile)) {
        setError(set(lensProp("thumbnail"), "An image must be selected"));
      }

      if (isBlank(type)) {
        setError(set(lensProp("type"), "A type must be selected"));
      }
    } else {
      cropper.getCroppedCanvas().toBlob(async blob => {
        const file = new File([blob], thumbnailFile.name, {
          lastModified: thumbnailFile.lastModified,
        });
        await onSave(type, file);
        reset();
      });
    }
  };

  const handleCancel = () => {
    onCancel();
    reset();
  };

  return (
    <MediaDialog
      open={visible}
      title="New Badge"
      maxWidth="lg"
      handleClose={handleCancel}
      content={
        <div className={classes.root}>
          <div className={classes.preview}>
            {uploading ? (
              <CircularProgress />
            ) : isBlank(thumbnailUrl) ? (
              <Box className={classes.thumbnailOverlay}>
                <PhotoOutlinedIcon className={classes.emptyThumbnail} />
              </Box>
            ) : (
              <ImageCrop
                aspectRatio={aspectRatio}
                thumbnailUrl={thumbnailUrl}
                currentUrl={null}
                imgSize={imgSize}
                cropper={cropper}
                handleInitialized={handleCropperInitialized}
              />
            )}
          </div>
          <div className={classes.input}>
            <div className={classes.title}>Upload a new badge</div>
            <p className={classes.subtitle}>
              Choose the image you wish to upload.
            </p>
            <p className={classes.fileTypes}>
              Accepted file types: .jpg, .jpeg, .png, .gif, .svg, image/jpeg,
              image/png, image/gif, image/svg+xml
            </p>
            <FileUploadInput
              value={thumbnailFile}
              accept=".jpg, .jpeg, .png, .gif, .svg, image/jpeg, image/png, image/gif, image/svg+xml"
              onChange={onFileInputChange}
              disabled={uploading}
              error={error.thumbnail}
            />

            <FormControl fullWidth error={notBlank(error.type)}>
              <InputLabel htmlFor="type-for-new-badge">Badge Type</InputLabel>
              <Select
                inputProps={{
                  name: "badge-type",
                  id: "type-for-new-badge",
                }}
                value={type}
                onChange={onTypeChange}
                MenuProps={{
                  transformOrigin: {
                    horizontal: "left",
                    vertical: "top",
                  },
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
              >
                <MenuItem value="award">Award</MenuItem>
                <MenuItem value="black">Black</MenuItem>
                <MenuItem value="white">White</MenuItem>
              </Select>
              {notBlank(error.type) && (
                <FormHelperText>{error.type}</FormHelperText>
              )}
            </FormControl>
          </div>
        </div>
      }
      actions={
        <Fragment>
          <Button disabled={uploading} onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            disabled={uploading}
            variant="contained"
            color="primary"
            onClick={handleSave}
          >
            Save
          </Button>
        </Fragment>
      }
    />
  );
};

export default UploadBadgeDialog;
